import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  Injector,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { catchError, debounce, EMPTY, firstValueFrom, Subject, timer } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EmployeeAdminService,
  EmployeeResponse,
} from '@nebalkon-workspace/admin-api';
import { ProfileService } from '@nebalkon-workspace/auth';
import { ClickOutsideDirective } from '@nebalkon-workspace/common';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import { NotInput, NotSystemNotificationService } from '@nebalkon-workspace/ui-kit';
import { StaffBirthdaysBannerComponent } from './staff-birthdays-banner.component';
import { bindStaffPageTitleCount } from './staff-page-title-count.util';
import { bindStaffPageHeaderAction } from './staff-page-header-action.util';

const PAGE_SIZE = 20;
/** Порог «у нижнего края» для подгрузки (px). */
const NEAR_BOTTOM_PX = 200;
/** Сколько раз подряд можно добивать экран при нехватке высоты скролла (без ручного скролла). */
const MAX_FILL_VIEWPORT_PAGES = 5;

@Component({
  selector: 'lib-staff-employees-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    ClickOutsideDirective,
    NotInput,
    FormsModule,
    CabinetButtonComponent,
    RouterLink,
    StaffBirthdaysBannerComponent,
  ],
  templateUrl: './staff-employees-list.component.html',
  styleUrl: './staff-employees-list.component.scss',
})
export class StaffEmployeesListComponent implements OnInit {
  readonly embeddedInCabinetPage = input(false);

  readonly #cabinetPaths = inject(CABINET_PATHS);
  protected readonly staffCabinetPath = this.#cabinetPaths.staff;
  readonly #employeeService = inject(EmployeeAdminService);
  readonly #router = inject(Router);
  readonly #translate = inject(TranslateService);
  readonly #profileService = inject(ProfileService);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #injector = inject(Injector);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly employeesListEl = viewChild<ElementRef<HTMLElement>>('employeesList');
  protected readonly headerAddActionTpl = viewChild<TemplateRef<unknown>>('headerAddAction');

  protected readonly searchQuery = signal('');
  protected readonly loading = signal(true);
  protected readonly loadingMore = signal(false);
  protected readonly initialized = signal(false);
  protected readonly openMenuId = signal<string | null>(null);
  protected readonly items = signal<EmployeeResponse[]>([]);
  protected readonly totalCount = signal(0);
  protected readonly activeTotalCount = signal(0);

  protected readonly displayCount = computed(() =>
    this.initialized() ? this.activeTotalCount() : null,
  );

  protected readonly currentUserId = computed(
    () => this.#profileService.userInfo()?.user.id,
  );

  protected readonly hasMore = computed(
    () => this.items().length < this.totalCount(),
  );

  constructor() {
    bindStaffPageTitleCount(this.embeddedInCabinetPage, this.displayCount);
    bindStaffPageHeaderAction(
      this.embeddedInCabinetPage,
      this.headerAddActionTpl,
      this.initialized,
    );
  }

  private readonly searchSubject = new Subject<string>();
  private lastSearch = '';
  private scrollListenerBound = false;
  private listScrollRoot?: HTMLElement;
  private scrollRaf = 0;
  /** Счётчик добивания экрана при коротком контенте (без скролла). */
  private fillViewportPages = 0;

  public ngOnInit(): void {

    this.#destroyRef.onDestroy(() => {
      if (this.scrollRaf) {
        cancelAnimationFrame(this.scrollRaf);
      }
      if (this.listScrollRoot) {
        this.listScrollRoot.removeEventListener('scroll', this.onListScroll);
      }
    });

    this.searchSubject.pipe(
      debounce((value) => (value ? timer(500) : timer(0))),
    ).subscribe((search) => {
      void this.reloadFirstPage(search);
    });

    this.searchSubject.next('');
  }

  protected onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.searchSubject.next(value);
  }

  private async reloadFirstPage(search: string): Promise<void> {
    this.loading.set(true);
    this.lastSearch = search;
    this.items.set([]);
    this.fillViewportPages = 0;
    try {
      const res = await firstValueFrom(
        this.#employeeService.getEmployees(search, {
          limit: PAGE_SIZE,
          offset: 0,
        }),
      );
      this.items.set(this.sortWithCurrentFirst(res.data));
      this.totalCount.set(res.total);
      this.activeTotalCount.set(res.active_total);
      this.initialized.set(true);
      afterNextRender(
        () => {
          this.ensureScrollListener();
          this.scheduleCheckNearBottom();
        },
        { injector: this.#injector },
      );
    } catch {
      this.initialized.set(true);
    } finally {
      this.loading.set(false);
    }
  }

  private ensureScrollListener(): void {
    const root = this.employeesListEl()?.nativeElement;
    if (!root || this.scrollListenerBound) {
      return;
    }
    this.scrollListenerBound = true;
    this.listScrollRoot = root;
    root.addEventListener('scroll', this.onListScroll, { passive: true });
  }

  private readonly onListScroll = (): void => {
    this.scheduleCheckNearBottom();
  };

  /** Проверка «у низа» + rAF, чтобы не дёргать расчёт на каждом событи скролла. */
  private scheduleCheckNearBottom(): void {
    if (this.scrollRaf) {
      cancelAnimationFrame(this.scrollRaf);
    }
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = 0;
      this.checkNearBottomOrFillViewport();
    });
  }

  /**
   * Подгрузка по расстоянию до низа скролл-контейнера (надёжнее IntersectionObserver в WebView).
   * Если контент ниже не прокручивается — ограниченно добиваем страницами.
   */
  private checkNearBottomOrFillViewport(): void {
    if (this.loading() || this.loadingMore() || !this.hasMore()) {
      return;
    }
    const root = this.employeesListEl()?.nativeElement;
    if (!root) {
      return;
    }

    const scrollHeight = root.scrollHeight;
    const clientHeight = root.clientHeight;
    const scrollTop = root.scrollTop;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    const contentDoesNotScroll =
      scrollHeight <= clientHeight + 4 && this.hasMore();

    if (contentDoesNotScroll && this.fillViewportPages < MAX_FILL_VIEWPORT_PAGES) {
      this.fillViewportPages++;
      this.fetchNextPage();
      return;
    }

    if (distanceFromBottom <= NEAR_BOTTOM_PX) {
      this.fetchNextPage();
    }
  }

  private fetchNextPage(): void {
    if (this.loading() || this.loadingMore() || !this.hasMore()) {
      return;
    }
    const offset = this.items().length;
    this.loadingMore.set(true);
    this.#employeeService
      .getEmployees(this.lastSearch, { limit: PAGE_SIZE, offset })
      .pipe(
        catchError(() => {
          this.loadingMore.set(false);
          return EMPTY;
        }),
      )
      .subscribe({
        next: (res) => {
          this.items.update((prev) => [...prev, ...res.data]);
          this.totalCount.set(res.total);
          this.activeTotalCount.set(res.active_total);
          this.loadingMore.set(false);
          afterNextRender(
            () => {
              this.checkNearBottomOrFillViewport();
            },
            { injector: this.#injector },
          );
        },
      });
  }

  protected onAdd(): void {
    void this.#router.navigate([this.#cabinetPaths.staff, 'employees', 'create']);
  }

  protected onEdit(id: string): void {
    this.openMenuId.set(null);
    void this.#router.navigate([`${this.#cabinetPaths.staff}/employees/${id}/edit`]);
  }

  protected async onDeactivate(id: string): Promise<void> {
    this.openMenuId.set(null);
    try {
      await firstValueFrom(this.#employeeService.deactivateEmployee(id));
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [this.#translate.instant('admin.employees.deactivateSuccess')],
      });
      this.searchSubject.next(this.searchQuery());
    } catch (error) {
      console.error('Failed to deactivate employee:', error);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.employees.deactivateError')],
      });
    }
  }

  protected async onActivate(id: string): Promise<void> {
    this.openMenuId.set(null);
    try {
      await firstValueFrom(this.#employeeService.activateEmployee(id));
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'success',
        text: [this.#translate.instant('admin.employees.activateSuccess')],
      });
      this.searchSubject.next(this.searchQuery());
    } catch (error) {
      console.error('Failed to activate employee:', error);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.employees.activateError')],
      });
    }
  }

  protected toggleMenu(id: string): void {
    this.openMenuId.set(this.openMenuId() === id ? null : id);
  }

  protected closeMenu(): void {
    this.openMenuId.set(null);
  }

  protected formatName(employee: EmployeeResponse): string {
    const ind = employee.individual;
    const lastName = ind.last_name
      ? ind.last_name.charAt(0).toUpperCase() + ind.last_name.slice(1)
      : '';
    const firstInitial = ind.first_name
      ? `${ind.first_name.charAt(0).toUpperCase()}.`
      : '';
    const patronymicInitial = ind.patronymic
      ? `${ind.patronymic.charAt(0).toUpperCase()}.`
      : '';

    return `${lastName} ${firstInitial}${patronymicInitial}`.trim();
  }

  private sortWithCurrentFirst(
    employees: EmployeeResponse[],
  ): EmployeeResponse[] {
    const currentId = this.currentUserId();
    if (!currentId) {
      return employees;
    }

    const currentUserIndex = employees.findIndex((emp) => emp.user.id === currentId);
    if (currentUserIndex === -1) {
      return employees;
    }

    const result = [...employees];
    const [currentUser] = result.splice(currentUserIndex, 1);
    return [currentUser, ...result];
  }
}
